<template>
    <div v-bind="$attrs">
        <div v-if="isAuthenticated" class="relative" ref="dropdownRef">
            <button
                @click="toggleNotifications"
                class="p-2 text-gray-600 transition-colors duration-200 rounded-full hover:text-gray-800 hover:bg-gray-100 dark:text-gray-400 dark:hover:text-gray-200 dark:hover:bg-gray-700">
                <BellIcon class="w-6 h-6" />
                <span
                    v-if="unreadCount > 0"
                    class="absolute inline-flex items-center justify-center text-xs font-bold text-white transform translate-x-1/2 -translate-y-1/2 bg-red-500 rounded-full right-1 top-2 size-5">
                    {{ unreadCount }}
                </span>
            </button>
        </div>

        <!-- Notifications Dropdown (Teleported to body to avoid clipping) -->
    <Teleport to="body">
        <div
            v-if="isOpen"
            class="notification-dropdown fixed z-[9999] mt-2 max-h-[500px] overflow-y-auto bg-white rounded-md shadow-lg w-80 dark:bg-gray-800 ring-1 ring-black ring-opacity-5"
            :style="dropdownStyle">
            <div class="p-4">
                <div class="flex items-center justify-between mb-4">
                    <h3 class="text-lg font-semibold text-gray-900 dark:text-gray-100">
                        Notifications
                    </h3>
                    <!-- <button
                v-if="unreadCount > 0"
                @click="markAllAsRead"
                class="text-sm text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300"
            >
                Mark all as read
            </button> -->
                    <RouterLink
                        :to="{name: ROUTES.NOTIFICATIONS}"
                        @click="isOpen = false"
                        class="text-sm text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300">
                        Show All Notifications
                    </RouterLink>
                </div>

                <div v-if="notifications.length === 0" class="text-center text-gray-500 dark:text-gray-400">
                    No notifications
                </div>

                <div v-else class="space-y-4 divide-y divide-gray-200 dark:divide-gray-700">
                    <RouterLink
                        :to="{name: ROUTES.NOTIFICATION, params: {id: notification.id}}"
                        @click="markAsRead(notification.id); isOpen = false;"
                        v-for="notification in notifications"
                        :key="notification.id"
                        class="block w-full p-3 transition-colors duration-200 rounded-lg"
                        :class="{
              'bg-gray-50 dark:bg-gray-700': !notification.read_at,
              'hover:bg-gray-100 dark:hover:bg-gray-600': true
            }">
                        <div class="flex items-start">
                            <div class="flex-1">
                                <p class="text-sm text-gray-900 dark:text-gray-100">
                                    {{ notification.data?.message || 'New notification' }}
                                </p>
                                <p v-if="notification.data?.type === 'order_status_changed'"
                                    class="text-xs text-gray-500 dark:text-gray-400">
                                    Order #{{ notification.data?.order_id }} - {{ notification.data?.business_name }}
                                </p>
                                <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                    {{ formatTime(notification.created_at) }}
                                </p>
                            </div>
                            <button
                                v-if="!notification.read_at"
                                @click="markAsRead(notification.id); isOpen = false;"
                                class="ml-2 text-gray-400 hover:text-gray-500 dark:text-gray-500 dark:hover:text-gray-400">
                                <CheckIcon class="w-4 h-4" />
                            </button>
                        </div>
                    </RouterLink>
                </div>
            </div>
        </div>
    </Teleport>
    </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { BellIcon, CheckIcon } from '@heroicons/vue/24/outline';
import { useNotifications } from '@/composables/useNotifications';

defineOptions({
  inheritAttrs: false
});

const dropdownRef = ref(null);
const isOpen = ref(false);
const dropdownPosition = ref({ top: 0, left: 0 });

const {
  notifications,
  unreadCount,
  isAuthenticated,
  fetchNotifications,
  markAsRead,
  setupNotificationListener,
  cleanupNotificationListener
} = useNotifications();

// Compute dropdown position based on button position
const dropdownStyle = computed(() => {
  return {
    top: `${dropdownPosition.value.top}px`,
    left: `${dropdownPosition.value.left}px`,
  };
});

const toggleNotifications = () => {
  isOpen.value = !isOpen.value;

  if (isOpen.value) {
    fetchNotifications();

    // Calculate position for the dropdown after a small delay to ensure DOM is updated
    setTimeout(() => {
      if (dropdownRef.value) {
        const rect = dropdownRef.value.getBoundingClientRect();
        const isMobile = window.innerWidth < 640; // sm breakpoint

        if (isMobile) {
          // On mobile, center the dropdown horizontally
          dropdownPosition.value = {
            top: rect.bottom + window.scrollY + 10,
            left: Math.max(10, window.innerWidth / 2 - 160) // 160 is half the width of the dropdown (320px/2)
          };
        } else {
          // On desktop, position it to the right of the bell
          dropdownPosition.value = {
            top: rect.bottom + window.scrollY + 10,
            left: Math.min(rect.right - 280, window.innerWidth - 300) // Keep it within viewport
          };
        }
      }
    }, 0);
  }
};

const formatTime = (timestamp) => {
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true
  });
};

function handleClickOutside(event) {
  // Only close if clicking outside both the bell button and the dropdown
  const dropdownElement = document.querySelector('.notification-dropdown');
  const clickedInsideDropdown = dropdownElement && dropdownElement.contains(event.target);
  const clickedInsideButton = dropdownRef.value && dropdownRef.value.contains(event.target);

  if (!clickedInsideButton && !clickedInsideDropdown) {
    isOpen.value = false;
  }
}

onMounted(() => {
  // Setup notification listener is already called in the composable
  document.addEventListener('mousedown', handleClickOutside);
});

onUnmounted(() => {
  // Cleanup notification listener is already called in the composable
  document.removeEventListener('mousedown', handleClickOutside);
});
</script>
